import { MeteringPointsState } from '@appStore/metering-points';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { MeteringPointGroup, Customer, MeteringPoint } from '@model';
import { CustomersState } from '@appStore/customers';
import { MeteringType } from '@enums';
import { sortByProperty } from '@utils/sx-utilities';

const getMeteringPointFeatureState = createFeatureSelector<MeteringPointsState>('meteringpoints');
const getMeteringPointsState = createSelector(
  getMeteringPointFeatureState,
  state => state.meteringPoints
);
const getMeteringPointGroupsState = createSelector(
  getMeteringPointFeatureState,
  state => state.meteringPointGroups
);

const getCustomerFeatureState = createFeatureSelector<CustomersState>('customers');
const getCustomerState = createSelector(
  getCustomerFeatureState,
  (state: CustomersState) => state.customers
);

export const getFilledMeteringPointGroups = createSelector(
  getMeteringPointsState,
  getMeteringPointGroupsState,
  (mpState, mpgState) => {
    const meteringPoints = mpState.data;
    const meteringPointGroups = mpgState.data;

    if (meteringPoints.length === 0) {
      return meteringPointGroups;
    }

    let mappedGroups = [] as MeteringPointGroup[];
    meteringPointGroups.forEach(mpg => {
      mappedGroups = meteringPointGroups.map(meteringPointGroup => {
        return {
          ...meteringPointGroup,
          meteringPoints: meteringPointGroup.meteringPoints.map(meteringPoint => {
            const lookup = meteringPoints.find(mp => mp.id === meteringPoint.id);
            if (lookup) {
              return { ...lookup };
            }
            return { ...meteringPoint };
          })
        };
      });
    });

    return <MeteringPointGroup[]>sortByProperty(mappedGroups, 'name');
  }
);

export const getMeteringPointsWithGroups = createSelector(
  getMeteringPointsState,
  getMeteringPointGroupsState,
  (mpState, mpgState) => {
    const meteringPointGroups = mpgState.data;
    let meteringPoints = mpState.data;
    if (meteringPoints.length > 0 && meteringPointGroups.length > 0) {
      meteringPoints = meteringPoints.map(mp => {
        const mpgs = meteringPointGroups.filter(mpg => mpg.meteringPoints.map(x => x.id).indexOf(mp.id) >= 0);
        return { ...mp, parentGroups: mpgs.map(d => ({ name: d.name, id: d.id })) };
      });
    }
    return meteringPoints;
  }
);

export const getSelectedMeteringPointWithGroups = createSelector(
  getMeteringPointsState,
  getMeteringPointGroupsState,
  (mpState, mpgState) => {
    const meteringPointGroups = mpgState.data;
    let selectedMeteringPoint = mpState.selected;
    if (selectedMeteringPoint && meteringPointGroups.length > 0) {
      const mpgs = meteringPointGroups.filter(
        mpg => mpg.meteringPoints.map(x => x.id).indexOf(selectedMeteringPoint.id) >= 0
      );
      return { ...selectedMeteringPoint, parentGroups: mpgs.map(d => ({ name: d.name, id: d.id })) };
    }
    return { ...selectedMeteringPoint, parentGroups: [] };
  }
);

export const getFilledCustomers = createSelector(
  getCustomerState,
  getMeteringPointsState,
  getMeteringPointGroupsState,
  (cState, mpState, mpgState) => {
    const customers = cState.data;
    const externalOrganisations = cState.externalOrganisations;
    const meteringPointGroups = mpgState.data;
    let meteringPoints = mpState.data;

    if (meteringPoints.length > 0 && meteringPointGroups.length > 0) {
      meteringPoints = meteringPoints.map(mp => {
        const mpgs = meteringPointGroups.filter(mpg => mpg.meteringPoints.map(x => x.id).indexOf(mp.id) >= 0);
        return { ...mp, parentGroups: mpgs.map(d => ({ name: d.name, id: d.id })) };
      });
    }

    if (customers.length > 0 && meteringPoints.length > 0) {
      const filledCustomers = [] as Customer[];

      customers.forEach(currentCustomer => {
        if (currentCustomer.meteringPointsCold || currentCustomer.meteringPointsHeat) {
          return;
        }

        let externalOrganisation = externalOrganisations?.find(x => x.id === currentCustomer.externalOrganisationId);

        const custData: Customer = {
          ...currentCustomer,
          externalOrganisationAlias: externalOrganisation ? externalOrganisation.alias : null,
          meteringPointsHeat: [
            ...meteringPoints.filter(x => x.customerId === currentCustomer.id && x.meteringType === MeteringType.Hot)
          ],
          meteringPointsCold: [
            ...meteringPoints.filter(x => x.customerId === currentCustomer.id && x.meteringType === MeteringType.Cold)
          ]
        };

        filledCustomers.push(custData);
      });
      return sortByProperty(filledCustomers, 'name');
    } else {
      return [];
    }
  }
);


export const getFilledCustomersWithContainedMeteringPoints = createSelector(
  getCustomerState,
  getMeteringPointsState,
  getMeteringPointGroupsState,
  (cState, mpState, mpgState) => {
    const customers = cState.data;
    const meteringPointGroups = mpgState.data;
    let meteringPoints = mpState.data;

    if (meteringPoints.length > 0 && meteringPointGroups.length > 0) {
      meteringPoints = meteringPoints.map(mp => {
        const mpgs = meteringPointGroups.filter(mpg => mpg.meteringPoints.map(x => x.id).indexOf(mp.id) >= 0);
        return { ...mp, parentGroups: mpgs.map(d => ({ name: d.name, id: d.id })) };
      });
    }

    if (customers.length > 0 && meteringPoints.length > 0) {
      const filledCustomers = [] as Customer[];

      customers.forEach(currentCustomer => {
        if (currentCustomer.meteringPointsCold || currentCustomer.meteringPointsHeat) {
          return;
        }

        // With Contained MeteringPoints
        let containedMeteringPoints: MeteringPoint[] = [];
        let logicalMeteringPoints: MeteringPoint[] = [];
        meteringPoints.forEach(mp => {
          if (mp.containedMeteringPoints && mp.containedMeteringPoints.length > 0) {

            logicalMeteringPoints.push(mp);
            mp.containedMeteringPoints.forEach(cmp => {
              containedMeteringPoints.push({...cmp, type: 'CONTAINED'});
            });
          }
        });

        let allHotMeteringPoints = meteringPoints.concat(containedMeteringPoints.map(c => c));
        allHotMeteringPoints = allHotMeteringPoints.filter((item) => !logicalMeteringPoints.includes(item));

        const custData: Customer = {
          ...currentCustomer,
          meteringPointsHeat: [
            ...allHotMeteringPoints.filter(x => x.customerId === currentCustomer.id && x.meteringType === MeteringType.Hot)
          ],
          meteringPointsCold: [
            ...meteringPoints.filter(x => x.customerId === currentCustomer.id && x.meteringType === MeteringType.Cold)
          ]
        };

        filledCustomers.push(custData);
      });
      return sortByProperty(filledCustomers, 'name');
    } else {
      return [];
    }
  }
);


export const getFilledCustomersWithDisruptions = createSelector(
  getCustomerState,
  getMeteringPointsState,
  getMeteringPointGroupsState,
  (cState, mpState, mpgState) => {
    const customers = cState.data;
    const meteringPointGroups = mpgState.data;
    let meteringPoints = mpState.data.filter(mp => mp.disruptionInfos);

    if (meteringPoints.length > 0 && meteringPointGroups.length > 0) {
      meteringPoints = meteringPoints.map(mp => {
        const mpgs = meteringPointGroups.filter(mpg => mpg.meteringPoints.map(x => x.id).indexOf(mp.id) >= 0);
        return { ...mp, parentGroups: mpgs.map(d => ({ name: d.name, id: d.id })) };
      });
    }

    if (customers.length > 0 && meteringPoints.length > 0) {
      const filledCustomers = [] as Customer[];

      customers.forEach(currentCustomer => {
        if (currentCustomer.meteringPointsCold || currentCustomer.meteringPointsHeat) {
          return;
        }

        const custData: Customer = {
          ...currentCustomer,
          meteringPointsHeat: [
            ...meteringPoints.filter(x => x.customerId === currentCustomer.id && x.meteringType === MeteringType.Hot)
          ],
          meteringPointsCold: [
            ...meteringPoints.filter(x => x.customerId === currentCustomer.id && x.meteringType === MeteringType.Cold)
          ]
        };
        if (custData.meteringPointsCold.length > 0 || custData.meteringPointsHeat.length > 0) {
          filledCustomers.push(custData);
        }
      });
      return sortByProperty(filledCustomers, 'name');
    } else {
      return [];
    }
  }
);
